import ITabletopDrawableElement from "../element/ITabletopDrawableElement";
import TabletopViewport from "../TabletopViewport";

import type { Point2d } from "../../../../common/geometry/Geom2d";
import TabletopGrid from "./TabletopGrid";

export default class TabletopHexGrid
    extends TabletopGrid
    implements ITabletopDrawableElement
{
    private _highlightCell: Point2d | null = null;
    public constructor(unitLength: number = 36) {
        super(unitLength);

        this._unitLength = unitLength;
    }

    /** Distance between the centers of two neighboring cells in the grid */
    public get unitLength() {
        return this._unitLength;
    }
    public set unitLength(val: number) {
        this._unitLength = val;
    }

    /** Distance between cell center and the midpoint of any of its sides (apothem) */
    public get innerRadius() {
        return this._unitLength / 2;
    }

    /** Distance between cell center and any vertex of the hexagon */
    public get outerRadius() {
        return this._unitLength / Math.sqrt(3);
    }

    /** Distance between any two neighboring vertices of the hexagon */
    public get sideLength() {
        return this.outerRadius;
    }

    public get horizontalSpacing() {
        return this.outerRadius * 1.5;
    }

    public get verticalSpacing() {
        return this._unitLength;
    }

    public get highlightCell() {
        return this._highlightCell;
    }

    public set highlightCell(val: Point2d | null) {
        this._highlightCell = val;
    }

    public getCellCoordinatesFromUniversePoint(point: Point2d): Point2d {
        const softXCoord = Math.floor(point.x / this.horizontalSpacing);
        const softYCoord = Math.floor(point.y / this.verticalSpacing);

        const softXCoords = [softXCoord, softXCoord + 1];
        const softYCoords = [softYCoord - 1, softYCoord, softYCoord + 1];

        const potentials = softXCoords.flatMap((testX) => {
            return softYCoords.map((testY) => {
                const testDistance = Math.sqrt(
                    Math.pow(point.x - testX * this.horizontalSpacing, 2) +
                        Math.pow(
                            point.y -
                                testY * this.verticalSpacing +
                                (testX % 2 === 0
                                    ? 0
                                    : this.verticalSpacing / 2),
                            2,
                        ),
                );

                return {
                    coord: { x: testX, y: testY },
                    distance: testDistance,
                };
            });
        });

        const shortestDistance = Math.min(...potentials.map((p) => p.distance));

        return (
            potentials.find((p) => p.distance === shortestDistance)?.coord ?? {
                x: softXCoord,
                y: softYCoord,
            }
        );
    }

    public getCellCoordinatesFromViewportPoint(
        point: Point2d,
        viewport: TabletopViewport,
    ): Point2d {
        return this.getCellCoordinatesFromUniversePoint(
            viewport.viewportPointToUniversePoint(point),
        );
    }

    public getUniversePointFromCellCoordinates(cellCoord: Point2d): Point2d {
        const xCoord = cellCoord.x * this.horizontalSpacing;
        const yCoord =
            cellCoord.x % 2 === 0
                ? cellCoord.y * this.verticalSpacing
                : cellCoord.y * this.verticalSpacing - this.verticalSpacing / 2;

        return { x: xCoord, y: yCoord };
    }

    public getViewportPointFromCellCoordinates(
        cellCoord: Point2d,
        viewport: TabletopViewport,
    ): Point2d {
        return viewport.universePointToViewportPoint(
            this.getUniversePointFromCellCoordinates(cellCoord),
        );
    }

    public snapUniversePointToGrid(point: Point2d): Point2d {
        return this.getUniversePointFromCellCoordinates(
            this.getCellCoordinatesFromUniversePoint(point),
        );
    }

    public snapViewportPointToGrid(
        point: Point2d,
        viewport: TabletopViewport,
    ): Point2d {
        return this.getViewportPointFromCellCoordinates(
            this.getCellCoordinatesFromViewportPoint(point, viewport),
            viewport,
        );
    }

    public draw(
        ctx: CanvasRenderingContext2D,
        viewport: TabletopViewport,
        timestamp: number,
    ): void {
        if (viewport.magnificationX < 0.5 || viewport.magnificationX > 25) {
            return;
        }

        const horizontalGap = this.horizontalSpacing * viewport.magnificationX;
        const verticalGap = this.verticalSpacing * viewport.magnificationY;
        const firstCellCoordinates = this.getCellCoordinatesFromViewportPoint(
            {
                x: 0,
                y: 0,
            },
            viewport,
        );
        const staggerCellCoordinates = {
            x: firstCellCoordinates.x + 1,
            y: firstCellCoordinates.y,
        };
        const firstOrigin = this.getViewportPointFromCellCoordinates(
            firstCellCoordinates,
            viewport,
        );
        const staggerOrigin = this.getViewportPointFromCellCoordinates(
            staggerCellCoordinates,
            viewport,
        );
        const gridAlpha = (
            viewport.magnificationX >= 0.75
                ? 255
                : Math.floor(((viewport.magnificationX - 0.5) / 0.25) * 256)
        )
            .toString(16)
            .padStart(2, "0");

        const gridColor = `#000000${gridAlpha}`;

        ctx.lineWidth = 1;
        ctx.strokeStyle = gridColor;

        for (
            let canvasX = firstOrigin.x,
                cellX = firstCellCoordinates.x,
                isStaggerCol = false;
            canvasX < ctx.canvas.width;
            canvasX += horizontalGap, ++cellX, isStaggerCol = !isStaggerCol
        ) {
            for (
                let canvasY = isStaggerCol ? staggerOrigin.y : firstOrigin.y,
                    cellY = isStaggerCol
                        ? staggerCellCoordinates.y
                        : firstCellCoordinates.y;
                canvasY < ctx.canvas.height + verticalGap;
                canvasY += verticalGap, ++cellY
            ) {
                const origin: Point2d = {
                    x: canvasX,
                    y: canvasY,
                };

                ctx.beginPath();

                for (let rad = 0; rad < Math.PI * 2; rad += (Math.PI * 2) / 6) {
                    const x = Math.cos(rad);
                    const y = Math.sin(rad);

                    ctx.lineTo(
                        origin.x +
                            x * this.outerRadius * viewport.magnificationX,
                        origin.y +
                            y * this.outerRadius * viewport.magnificationY,
                    );
                }

                if (
                    this._highlightCell != null &&
                    this._highlightCell.x === cellX &&
                    this._highlightCell?.y === cellY
                ) {
                    ctx.lineWidth = 3;
                    ctx.stroke();
                    ctx.lineWidth = 1;
                } else {
                    ctx.stroke();
                }

                if (this.cellHighlights.length > 0) {
                    if (this.cellHighlights.get(cellX)?.hasKey(cellY)) {
                        ctx.fillStyle =
                            this.cellHighlights.get(cellX)?.get(cellY) ?? "#00";
                        ctx.fill();
                    }
                }

                // ctx.strokeText(`${cellX}:${cellY}`, origin.x, origin.y);
            }
        }
    }
}
